// src/scenes/vendors/Vendors.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
    Box,
    Button,
    IconButton,
    Modal,
    Typography,
    CircularProgress,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    useTheme,
    Stack,
    Divider,
    Snackbar,
    Alert,
} from "@mui/material";
import { alpha, darken, lighten, styled } from "@mui/material/styles";
import { DataGridPro, GridToolbar, useGridApiContext } from "@mui/x-data-grid-pro";
import { LicenseInfo } from "@mui/x-license-pro";
import Header from "../../components/Header";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import MergeIcon from "@mui/icons-material/CallMerge";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { tokens } from "../../theme";
import NewVendor from "../../component-files/Forms/NewVendor";

/* ───────────────── MUI X Pro license (if provided) ───────────────── */
try {
    const key =
        (typeof import.meta !== "undefined" && import.meta.env?.VITE_MUIX_LICENSE_KEY) ||
        (typeof process !== "undefined" && process.env?.REACT_APP_MUIX_LICENSE_KEY) ||
        "";
    if (key) LicenseInfo.setLicenseKey(key);
} catch { /* no-op */ }

/* ─────────────── API endpoints ─────────────── */
const API_ENDPOINT_VENDORS = "https://figazuresql.swancreate.com/api/swa/FINANCE_vendors";
const API_ENDPOINT_CATEGORIES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Categories";
const API_ENDPOINT_SUBCATEGORIES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Subcategories";
const API_REIMBURSEMENT_PROGRAMS = "https://figazuresql.swancreate.com/api/swa/FINANCE_ReimbursementPrograms";
const API_ENDPOINT_VENDOR_PRODUCTS = "https://figazuresql.swancreate.com/api/swa/FINANCE_VendorProduct";

/* ─────────────── Helpers ─────────────── */
const toIntOrNull = (v) => {
    if (v === "" || v === undefined || v === null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const GUID_KEYS = new Set(["VendorID", "SchoolDistrictId", "CreatedBy", "UpdatedBy", "DistributorID"]);
const INT_KEYS = new Set(["CategoryID", "SubCategoryID", "ReimbursementTrackingId"]);
const DECIMAL_KEYS = new Set(["BudgetAmount"]);
const DROP_KEYS = new Set(["VendorID", "CreatedBy", "UpdatedBy", "DistrictName", "LogoImage", "CompanyDocumentURL"]);

const isGuidKeyName = (k) =>
    GUID_KEYS.has(k) || (/(^|.*)(Id|ID|Guid|GUID)$/.test(k) && !INT_KEYS.has(k));

function normalizeForApi(row) {
    const payload = { ...row };

    // never send apiid in body; it's in the URL
    delete payload.apiid;

    for (const k of DROP_KEYS) if (k in payload) delete payload[k];
    for (const k of Object.keys(payload)) if (payload[k] === undefined) payload[k] = null;

    for (const k of Object.keys(payload)) {
        if (isGuidKeyName(k)) {
            const v = payload[k];
            if (v === "" || v === null) payload[k] = null;
            else if (typeof v === "string" && !GUID_REGEX.test(v)) payload[k] = null;
        }
    }
    for (const k of INT_KEYS) {
        if (k in payload) payload[k] = toIntOrNull(payload[k]);
    }
    for (const k of DECIMAL_KEYS) {
        if (k in payload) {
            const v = payload[k];
            if (v === "" || v === null) payload[k] = null;
            else if (typeof v === "string" || typeof v === "number") {
                const n = typeof v === "string" ? parseFloat(v.replace(/[, ]/g, "")) : Number(v);
                payload[k] = Number.isFinite(n) ? n : null;
            }
        }
    }
    if ("isActive" in payload) {
        const v = payload.isActive;
        payload.isActive = v === true || v === false ? v : String(v).toLowerCase() === "true";
    }

    return payload;
}

/* ─────────────── Styled Pro Grid ─────────────── */
const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    border: "none",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    "& .MuiDataGrid-columnHeaders": {
        backgroundColor:
            theme.palette.mode === "dark"
                ? darken(theme.palette.background.paper, 0.08)
                : lighten(theme.palette.background.paper, 0.04),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiDataGrid-row--even": {
        backgroundColor:
            theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.light, 0.12)
                : alpha(theme.palette.primary.main, 0.04),
    },
    "& .MuiDataGrid-row--odd": {
        backgroundColor:
            theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.main, 0.08)
                : alpha(theme.palette.primary.light, 0.08),
    },
    "& .MuiDataGrid-cell": {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiDataGrid-footerContainer": {
        backgroundColor:
            theme.palette.mode === "dark"
                ? darken(theme.palette.background.paper, 0.08)
                : lighten(theme.palette.background.paper, 0.04),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}));

/* ─────────────── Inline Select Editor that PATCHes immediately ─────────────── */
function QuickSelectEditCell(props) {
    const { id, field, value, colDef, row, onCommitChange } = props;
    const apiRef = useGridApiContext();

    const options =
        typeof colDef.valueOptions === "function"
            ? colDef.valueOptions({ id, field, value, row, colDef })
            : colDef.valueOptions || [];

    // keep "" to avoid out-of-range warning when null
    const editorValue = value ?? "";

    const handleChange = async (e) => {
        const raw = e.target.value; // "" or number/string
        console.groupCollapsed(`[EditCell change] ${field}`);
        console.log("row.apiid:", row?.apiid);
        console.log("raw:", raw);
        console.groupEnd();

        // Update UI model then leave edit mode
        await apiRef.current.setEditCellValue({ id, field, value: raw }, e);
        apiRef.current.stopCellEditMode({ id, field });

        // Immediately persist
        try {
            await onCommitChange?.({ id, field, value: raw, row });
        } catch (err) {
            console.error("[EditCell change → PATCH error]", err);
        }
    };

    return (
        <Select
            fullWidth
            size="small"
            autoFocus
            displayEmpty
            value={editorValue}
            onChange={handleChange}
            MenuProps={{ disableScrollLock: true }}
        >
            <MenuItem value="">
                <em>—</em>
            </MenuItem>
            {options.map((o) => (
                <MenuItem key={String(o.value)} value={o.value}>
                    {o.label ?? o.value}
                </MenuItem>
            ))}
        </Select>
    );
}

export default function Vendors() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { user } = useAuth();

    const [vendorData, setVendorData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errText, setErrText] = useState("");

    const [isVendorEntryOpen, setIsVendorEntryOpen] = useState(false);
    const [isMergeOpen, setIsMergeOpen] = useState(false);

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [reimbursementPrograms, setReimbursementPrograms] = useState([]);

    const [searchTerm, setSearchTerm] = useState("");

    // Merge state
    const [mergeTargetId, setMergeTargetId] = useState("");
    const [mergeSourceIds, setMergeSourceIds] = useState([]);
    const [deleteSourcesAfterMerge, setDeleteSourcesAfterMerge] = useState(true);
    const [mergeBusy, setMergeBusy] = useState(false);

    // install axios interceptors once
    const interceptorsInstalled = useRef(false);
    useEffect(() => {
        if (interceptorsInstalled.current) return;
        interceptorsInstalled.current = true;

        axios.interceptors.request.use((config) => {
            if (/\/FINANCE_vendors\//i.test(config.url) && config.method?.toLowerCase() === "patch") {
                console.groupCollapsed("HTTP PATCH → " + config.url);
                try {
                    console.log("payload:", JSON.parse(config.data));
                } catch {
                    console.log("payload (raw):", config.data);
                }
                console.groupEnd();
            }
            return config;
        });

        axios.interceptors.response.use(
            (resp) => {
                if (/\/FINANCE_vendors\//i.test(resp.config.url) && resp.config.method?.toLowerCase() === "patch") {
                    console.groupCollapsed("HTTP PATCH ← " + resp.config.url);
                    console.log("status:", resp.status);
                    console.log("data:", resp.data);
                    console.groupEnd();
                }
                return resp;
            },
            (err) => {
                const cfg = err?.config || {};
                if (/\/FINANCE_vendors\//i.test(cfg.url || "") && (cfg.method || "").toLowerCase() === "patch") {
                    console.groupCollapsed("HTTP PATCH ERROR ← " + cfg.url);
                    console.error("status:", err?.response?.status);
                    console.error("data:", err?.response?.data);
                    console.error("message:", err?.message);
                    console.groupEnd();
                }
                return Promise.reject(err);
            }
        );
    }, []);

    /* ===================== LOAD DATA ===================== */
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [vendRes, catRes, subRes, progRes] = await Promise.all([
                    axios.get(API_ENDPOINT_VENDORS),
                    axios.get(API_ENDPOINT_CATEGORIES),
                    axios.get(API_ENDPOINT_SUBCATEGORIES),
                    axios.get(API_REIMBURSEMENT_PROGRAMS),
                ]);

                const cats = (catRes.data || []).filter((c) => c.Type === "Expense");
                const subs = (subRes.data || []).filter((s) => s.Type === "Expense");
                setCategories(cats);
                setSubcategories(subs);
                setReimbursementPrograms(progRes.data || []);

                const vendors = (vendRes.data || []).map((v) => ({
                    ...v,
                    SchoolDistrictId: v.SchoolDistrictId ?? user?.SchoolDistrictId ?? null,
                    CategoryID: toIntOrNull(v.CategoryID),
                    SubCategoryID: toIntOrNull(v.SubCategoryID),
                }));
                setVendorData(vendors);
            } catch (err) {
                console.error(err);
                setErrText("Failed to load vendor data.");
            } finally {
                setLoading(false);
            }
        })();
    }, [user]);

    const fetchVendors = useCallback(async () => {
        try {
            const res = await axios.get(API_ENDPOINT_VENDORS);
            const vendors = (res.data || []).map((v) => ({
                ...v,
                SchoolDistrictId: v.SchoolDistrictId ?? user?.SchoolDistrictId ?? null,
                CategoryID: toIntOrNull(v.CategoryID),
                SubCategoryID: toIntOrNull(v.SubCategoryID),
            }));
            setVendorData(vendors);
        } catch (e) {
            console.error(e);
            setErrText("Failed to reload vendors.");
        }
    }, [user]);

    /* ===================== UTIL MAPS ===================== */
    const catNameById = useMemo(() => {
        const m = new Map();
        categories.forEach((c) => m.set(Number(c.CategoryID), c.CategoryName));
        return m;
    }, [categories]);

    const subsByCat = useMemo(() => {
        const m = new Map();
        for (const s of subcategories) {
            const cid = Number(s.CategoryID);
            if (!m.has(cid)) m.set(cid, []);
            m.get(cid).push(s);
        }
        return m;
    }, [subcategories]);

    /* ===================== VENDOR PRODUCTS (merge) ===================== */
    const fetchAllVendorProducts = async () => {
        const res = await axios.get(API_ENDPOINT_VENDOR_PRODUCTS);
        return Array.isArray(res.data) ? res.data : [];
    };

    const updateProductsVendorRef = async ({ sourceVendorIds, targetVendor }) => {
        const all = await fetchAllVendorProducts();
        const sourceSet = new Set(sourceVendorIds.map(String));
        const toUpdate = all.filter((p) => sourceSet.has(String(p.VendorId)));
        for (const p of toUpdate) {
            const payload = { VendorId: targetVendor.apiid, VendorName: targetVendor.VendorName };
            await axios.patch(`${API_ENDPOINT_VENDOR_PRODUCTS}/${p.apiid}`, payload);
        }
        return toUpdate.length;
    };

    const deleteProductsForVendor = async (vendorId) => {
        const all = await fetchAllVendorProducts();
        const mine = all.filter((p) => String(p.VendorId) === String(vendorId));
        for (const p of mine) await axios.delete(`${API_ENDPOINT_VENDOR_PRODUCTS}/${p.apiid}`);
        return mine.length;
    };

    /* ===================== DELETE VENDOR ===================== */
    const handleDeleteClick = async (apiid) => {
        try {
            await deleteProductsForVendor(apiid);
            await axios.delete(`${API_ENDPOINT_VENDORS}/${apiid}`);
            setVendorData((v) => v.filter((x) => x.apiid !== apiid));
        } catch (e) {
            console.error(e);
            setErrText("Failed to delete vendor and/or associated products.");
        }
    };

    /* ===================== PATCH helper (LOUD LOGS) ===================== */
    const patchVendor = useCallback(
        async ({ apiid, field, value, currentRow }) => {
            // Build minimal payload
            const payload = { SchoolDistrictId: currentRow?.SchoolDistrictId ?? null };

            if (field === "CategoryID") {
                payload.CategoryID = toIntOrNull(value);
                payload.CategoryName =
                    payload.CategoryID != null ? (catNameById.get(Number(payload.CategoryID)) || "") : "";
            } else if (field === "SubCategoryID") {
                const subId = toIntOrNull(value);
                payload.SubCategoryID = subId;
                if (subId != null) {
                    const s = subcategories.find((x) => Number(x.SubcategoryID) === subId);
                    payload.SubCategoryName = s?.SubcategoryName || "";
                } else {
                    payload.SubCategoryName = "";
                }
            } else if (field === "ReimbursmentTrackingProgram") {
                payload.ReimbursmentTrackingProgram = value == null ? "" : String(value);
            } else if (field === "BudgetAmount") {
                payload.BudgetAmount =
                    value === "" || value == null ? null : Number(String(value).replace(/[, ]/g, ""));
            } else if (field === "BlanketPONumber") {
                payload.BlanketPONumber = value ?? "";
            } else if (field === "isActive") {
                payload.isActive = !!value;
            } else {
                // fallback: send exactly that field
                payload[field] = value ?? null;
            }

            console.groupCollapsed("[PATCH Vendors]");
            console.log("apiid:", apiid, "payload:", payload);
            console.groupEnd();

            const normalized = normalizeForApi({ ...payload });

            const resp = await axios.patch(`${API_ENDPOINT_VENDORS}/${apiid}`, normalized, {
                headers: { "Content-Type": "application/json" },
                transformRequest: [(data) => JSON.stringify(data)],
            });

            console.log(`[PATCH OK] field: ${field} value:`, value, "resp:", resp?.data);
            return resp?.data;
        },
        [catNameById, subcategories]
    );

    /* ===================== Commit change (called by editor immediately) ===================== */
    const commitFieldChange = useCallback(
        async ({ id: apiid, field, value, row }) => {
            // normalize comparison
            const current = vendorData.find((r) => r.apiid === apiid);
            if (!current) return;

            let normalized = value;
            if (field === "CategoryID" || field === "SubCategoryID") {
                normalized = value === "" || value == null ? null : Number(value);
            } else if (field === "ReimbursmentTrackingProgram") {
                normalized = value == null ? "" : String(value);
            }

            const prev = current[field];
            const changed =
                field === "ReimbursmentTrackingProgram"
                    ? String(prev ?? "") !== String(normalized ?? "")
                    : prev !== normalized;

            if (!changed) {
                console.log("[Immediate PATCH] value not changed; skipping.");
                return;
            }

            // Optimistic UI update
            const nextRow = { ...current, [field]: normalized };
            if (field === "CategoryID") {
                nextRow.CategoryName = normalized != null ? (catNameById.get(Number(normalized)) || "") : "";
            }
            if (field === "SubCategoryID") {
                const found =
                    normalized != null
                        ? subcategories.find((s) => Number(s.SubcategoryID) === Number(normalized))
                        : null;
                nextRow.SubCategoryName = found?.SubcategoryName || "";
            }
            setVendorData((prev) => prev.map((r) => (r.apiid === apiid ? nextRow : r)));

            try {
                await patchVendor({ apiid, field, value: normalized, currentRow: current });
            } catch (e) {
                setErrText(e?.response?.data?.error || "Update failed. Reverted.");
                // Revert
                setVendorData((prev) => prev.map((r) => (r.apiid === apiid ? current : r)));
            }
        },
        [vendorData, catNameById, subcategories, patchVendor]
    );

    /* ===================== SUB OPTIONS ===================== */
    const subOptionsFor = useCallback(
        (row) => {
            const cid = toIntOrNull(row?.CategoryID);
            const base = cid != null ? (subsByCat.get(cid) || []) : [];
            const options = base.map((s) => ({ value: Number(s.SubcategoryID), label: s.SubcategoryName }));
            const currentSubId = toIntOrNull(row?.SubCategoryID);
            if (currentSubId != null && !options.some((o) => Number(o.value) === currentSubId)) {
                const orphan = subcategories.find((s) => Number(s.SubcategoryID) === currentSubId);
                options.unshift({
                    value: currentSubId,
                    label: orphan ? orphan.SubcategoryName : `#${currentSubId}`,
                });
            }
            return options;
        },
        [subsByCat, subcategories]
    );

    /* ===================== NON-SELECT CELLS: patch on stop ===================== */
    const handleCellEditStop = useCallback(
        async (params, event) => {
            const { id: apiid, field, value, reason } = params;
            console.log("[Grid] onCellEditStop", { apiid, field, value, reason });

            // Only handle non-select fields here. Selects are patched by the editor already.
            const isSelect =
                field === "CategoryID" || field === "SubCategoryID" || field === "ReimbursmentTrackingProgram";
            if (isSelect) return;

            const current = vendorData.find((r) => r.apiid === apiid);
            if (!current) return;

            // no change? skip
            if ((current[field] ?? "") === (value ?? "")) return;

            // optimistic UI
            const next = { ...current, [field]: value };
            setVendorData((prev) => prev.map((r) => (r.apiid === apiid ? next : r)));

            try {
                await patchVendor({ apiid, field, value, currentRow: current });
            } catch (e) {
                setErrText(e?.response?.data?.error || "Update failed. Reverted.");
                setVendorData((prev) => prev.map((r) => (r.apiid === apiid ? current : r)));
            }
        },
        [vendorData, patchVendor]
    );

    /* ===================== COLUMNS ===================== */
    const columns = useMemo(
        () => [
            { field: "VendorName", headerName: "Vendor", flex: 1, editable: true },
            { field: "Address", headerName: "Address", flex: 1, editable: true },
            { field: "City", headerName: "City", flex: 0.7, editable: true },
            { field: "State", headerName: "State", flex: 0.5, editable: true },
            { field: "ZipCode", headerName: "Zip", flex: 0.5, editable: true },

            {
                field: "BlanketPONumber",
                headerName: "Blanket PO",
                flex: 0.9,
                editable: true,
            },
            {
                field: "BudgetAmount",
                headerName: "Budget",
                flex: 0.8,
                type: "number",
                editable: true,
                valueFormatter: ({ value }) =>
                    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0),
            },

            // CATEGORY (immediate PATCH via editor)
            {
                field: "CategoryID",
                headerName: "Category",
                flex: 1,
                editable: true,
                type: "singleSelect",
                valueOptions: [{ value: "", label: "—" }, ...categories.map((c) => ({
                    value: Number(c.CategoryID),
                    label: c.CategoryName,
                }))],
                valueParser: (v) => (v === "" || v === null ? null : Number(v)),
                renderEditCell: (params) => <QuickSelectEditCell {...params} onCommitChange={commitFieldChange} />,
            },

            // SUBCATEGORY (immediate PATCH via editor)
            {
                field: "SubCategoryID",
                headerName: "Subcategory",
                flex: 1,
                editable: true,
                type: "singleSelect",
                valueOptions: (params) => [{ value: "", label: "—" }, ...subOptionsFor(params.row)],
                valueParser: (v) => (v === "" || v === null ? null : Number(v)),
                renderEditCell: (params) => <QuickSelectEditCell {...params} onCommitChange={commitFieldChange} />,
            },

            // PROGRAM (immediate PATCH via editor)
            {
                field: "ReimbursmentTrackingProgram",
                headerName: "Program",
                flex: 1,
                editable: true,
                type: "singleSelect",
                valueOptions: [{ label: "—", value: "" }, ...reimbursementPrograms.map((p) => ({
                    label: p.program_abbreviation,
                    value: p.program_abbreviation,
                }))],
                valueParser: (v) => (v == null ? "" : String(v)),
                renderEditCell: (params) => <QuickSelectEditCell {...params} onCommitChange={commitFieldChange} />,
            },

            {
                field: "isActive",
                headerName: "Active",
                type: "boolean",
                flex: 0.5,
                editable: true,
            },

            {
                field: "delete",
                headerName: "Delete",
                sortable: false,
                filterable: false,
                width: 80,
                renderCell: (params) => (
                    <IconButton onClick={() => handleDeleteClick(params.row.apiid)}>
                        <DeleteIcon />
                    </IconButton>
                ),
            },
        ],
        [categories, subOptionsFor, reimbursementPrograms, commitFieldChange]
    );

    /* ===================== FILTERED ROWS ===================== */
    const filtered = vendorData.filter((v) =>
        (v.VendorName || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    /* ===================== UI ===================== */
    return (
        <Box
            m="20px"
            sx={{
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                minHeight: "100vh",
                p: 2,
                borderRadius: 2,
                boxShadow: `0 0 30px 5px ${theme.palette.divider}`,
            }}
        >
            <Header title="VENDORS" subtitle="Manage Vendor Information" />

            <Box
                sx={{
                    p: 3,
                    mb: 2,
                    borderRadius: 3,
                    background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    color: "#fff",
                    boxShadow: `0 0 15px 4px ${theme.palette.secondary.main}`,
                }}
            >
                <Typography variant="h6" gutterBottom>How to use this page</Typography>
                <Typography variant="body2" component="div" sx={{ lineHeight: 1.6 }}>
                    1. <b>Add New Vendor</b> to create a vendor.<br />
                    2. Use <b>Search Vendor</b> to filter the table by name.<br />
                    3. <b>Edit</b> cells inline; changes save on selection (for pickers) or when the cell closes (text/boolean).<br />
                    4. <b>Merge Vendors</b>: choose a <i>target</i> and one or more <i>sources</i>. Products from sources move to the target.<br />
                    5. <b>Delete</b> removes a vendor and cascades its products.
                </Typography>
            </Box>

            <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
                <Button onClick={() => setIsVendorEntryOpen(true)} variant="contained" color="success">
                    Add New Vendor
                </Button>

                <Button
                    onClick={() => setIsMergeOpen(true)}
                    variant="outlined"
                    startIcon={<MergeIcon />}
                    sx={(theme) => ({
                        backgroundColor:
                            theme.palette.mode === "dark" ? theme.palette.grey[100] : theme.palette.grey[900],
                        color: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[50],
                        borderColor:
                            theme.palette.mode === "dark" ? theme.palette.grey[300] : theme.palette.grey[700],
                        "&:hover": {
                            backgroundColor:
                                theme.palette.mode === "dark" ? theme.palette.grey[200] : theme.palette.grey[800],
                            borderColor:
                                theme.palette.mode === "dark" ? theme.palette.grey[400] : theme.palette.grey[600],
                        },
                        "& .MuiButton-startIcon": { color: "inherit" },
                    })}
                >
                    Merge Vendors
                </Button>

                <TextField
                    variant="outlined"
                    label="Search Vendor"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 1 }}
                    InputProps={{ style: { color: theme.palette.text.primary } }}
                />
            </Box>

            {loading ? (
                <CircularProgress />
            ) : (
                <Box height="75vh">
                    <StyledDataGrid
                        rows={filtered}
                        columns={columns}
                        getRowId={(r) => r.apiid}
                        slots={{ Toolbar: GridToolbar }}
                        slotProps={{ toolbar: { showQuickFilter: true } }}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } },
                            pinnedColumns: { right: ["delete"] },
                        }}
                        pageSizeOptions={[10, 20, 50]}
                        disableRowSelectionOnClick
                        density="compact"
                        getRowClassName={(params) =>
                            params.indexRelativeToCurrentPage % 2 === 0
                                ? "MuiDataGrid-row--even"
                                : "MuiDataGrid-row--odd"
                        }
                        // SELECT fields patch immediately in QuickSelectEditCell.
                        // Other editable fields patch when edit stops:
                        onCellEditStop={handleCellEditStop}
                        columnBuffer={6}
                    />
                </Box>
            )}

            {/* Create/Edit Vendor Modal */}
            <Modal
                open={isVendorEntryOpen}
                onClose={() => setIsVendorEntryOpen(false)}
                sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: "90vw",
                        height: "90vh",
                        bgcolor: theme.palette.background.paper,
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 24,
                        overflow: "auto",
                    }}
                >
                    <IconButton onClick={() => setIsVendorEntryOpen(false)} sx={{ position: "absolute", top: 8, right: 8 }}>
                        <CloseIcon />
                    </IconButton>
                    <NewVendor
                        onClose={() => setIsVendorEntryOpen(false)}
                        onSaved={async () => {
                            setIsVendorEntryOpen(false);
                            await fetchVendors();
                        }}
                    />
                </Box>
            </Modal>

            {/* Merge Vendors Modal */}
            <Modal
                open={isMergeOpen}
                onClose={() => !mergeBusy && setIsMergeOpen(false)}
                sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: 700,
                        maxWidth: "95vw",
                        bgcolor: theme.palette.background.paper,
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 24,
                    }}
                >
                    <IconButton
                        onClick={() => !mergeBusy && setIsMergeOpen(false)}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                        disabled={mergeBusy}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography variant="h6" sx={{ mb: 1 }}>Merge Vendors</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Move all products from the <strong>source vendor(s)</strong> to the <strong>target vendor</strong>.
                        Optionally delete the source vendors after merge.
                    </Typography>

                    {errText && <Typography color="error" sx={{ mb: 1 }}>{errText}</Typography>}

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <FormControl fullWidth size="small">
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Target vendor</Typography>
                            <Select
                                value={mergeTargetId}
                                onChange={(e) => setMergeTargetId(e.target.value)}
                                displayEmpty
                                disabled={mergeBusy}
                                sx={{ backgroundColor: theme.palette.background.default }}
                            >
                                <MenuItem value="">
                                    <em>Select target…</em>
                                </MenuItem>
                                {vendorData.map((v) => (
                                    <MenuItem key={v.apiid} value={v.apiid}>
                                        {v.VendorName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Source vendor(s)</Typography>
                            <Select
                                multiple
                                value={mergeSourceIds}
                                onChange={(e) => setMergeSourceIds(e.target.value)}
                                renderValue={(selected) => {
                                    if (!selected.length) return "Select source(s)…";
                                    const names = vendorData
                                        .filter((v) => selected.includes(String(v.apiid)))
                                        .map((v) => v.VendorName);
                                    return names.join(", ");
                                }}
                                disabled={mergeBusy}
                                sx={{ backgroundColor: theme.palette.background.default }}
                            >
                                {vendorData
                                    .filter((v) => String(v.apiid) !== String(mergeTargetId))
                                    .map((v) => (
                                        <MenuItem key={v.apiid} value={v.apiid}>
                                            <Checkbox checked={mergeSourceIds.indexOf(v.apiid) > -1} />
                                            <ListItemText primary={v.VendorName} />
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                        <Checkbox
                            checked={deleteSourcesAfterMerge}
                            onChange={(e) => setDeleteSourcesAfterMerge(e.target.checked)}
                            disabled={mergeBusy}
                        />
                        <Typography variant="body2">Delete source vendors after merge</Typography>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button onClick={() => setIsMergeOpen(false)} disabled={mergeBusy}>
                            Cancel
                        </Button>
                        <Button
                            onClick={async () => {
                                if (!mergeTargetId || mergeSourceIds.length === 0) {
                                    setErrText("Select a target and at least one source to merge.");
                                    return;
                                }
                                if (mergeSourceIds.includes(mergeTargetId)) {
                                    setErrText("Sources cannot include the target vendor.");
                                    return;
                                }
                                const targetVendor = vendorData.find((v) => String(v.apiid) === String(mergeTargetId));
                                const sources = vendorData.filter((v) => mergeSourceIds.includes(String(v.apiid)));
                                if (!targetVendor || sources.length === 0) {
                                    setErrText("Invalid selection for merge.");
                                    return;
                                }

                                setMergeBusy(true);
                                setErrText("");
                                try {
                                    await updateProductsVendorRef({
                                        sourceVendorIds: sources.map((s) => s.apiid),
                                        targetVendor,
                                    });

                                    if (deleteSourcesAfterMerge) {
                                        for (const s of sources) await axios.delete(`${API_ENDPOINT_VENDORS}/${s.apiid}`);
                                        setVendorData((prev) => prev.filter((v) => !mergeSourceIds.includes(String(v.apiid))));
                                    }
                                    setIsMergeOpen(false);
                                } catch (e) {
                                    console.error(e);
                                    setErrText("Merge failed. Check console for details.");
                                } finally {
                                    setMergeBusy(false);
                                }
                            }}
                            variant="contained"
                            color="primary"
                            disabled={mergeBusy || !mergeTargetId || mergeSourceIds.length === 0}
                        >
                            {mergeBusy ? "Merging…" : "Merge"}
                        </Button>
                    </Stack>
                </Box>
            </Modal>

            {/* Error snackbar */}
            <Snackbar
                open={!!errText}
                autoHideDuration={5000}
                onClose={() => setErrText("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setErrText("")} severity="error" variant="filled">
                    {errText}
                </Alert>
            </Snackbar>
        </Box>
    );
}
